// dataEntryApi.js — fetch helper for the Data Entry views.
//
// ClickHouse Cloud idles and takes time to wake; the backend returns HTTP 503 while
// it's starting up. This helper keeps retrying on 503 and on network errors (backend
// not reachable yet) so the views can "keep loading until it's turned on". All Data
// Entry endpoints are idempotent (GET, replace-scope POST, DELETE), so retrying is safe.
// Real client/server errors (4xx / 500) surface immediately instead of being retried.
export const API_BASE = process.env.REACT_APP_API_BASE || 'http://localhost:8010';
const sleep = (ms) => new Promise((r) => setTimeout(r, ms));

// 502 and 504 join 503: on Render an instance that ran out of memory and is restarting answers with
// an empty 502, and a request that outlived a cold start with a 504 — both are "not up yet", not
// "this request is wrong".
const WAKING = new Set([502, 503, 504]);

export async function deFetch(path, { method = 'GET', body, retries = 15, delay = 3000, onWaking, signal } = {}) {
  let lastErr;
  for (let attempt = 0; attempt < retries; attempt++) {
    if (signal && signal.aborted) throw new DOMException('Aborted', 'AbortError');
    let res;
    try {
      res = await fetch(`${API_BASE}${path}`, {
        method,
        headers: body ? { 'Content-Type': 'application/json' } : undefined,
        body: body ? JSON.stringify(body) : undefined,
        signal,
      });
    } catch (e) {
      // A page that went away (or a newer selection) cancelled it — never retry that.
      if (e && e.name === 'AbortError') throw e;
      // Network error (backend not up yet / transient) → treat as "waking" and retry.
      lastErr = e;
      if (onWaking) onWaking(attempt);
      await sleep(delay);
      continue;
    }

    if (WAKING.has(res.status)) {             // ClickHouse / the instance still waking → retry
      if (onWaking) onWaking(attempt);
      lastErr = new Error('ClickHouse is starting up');
      await sleep(delay);
      continue;
    }
    if (!res.ok) throw new Error((await res.text()) || `HTTP ${res.status}`); // 4xx/500 → surface now
    const text = await res.text();
    return text ? JSON.parse(text) : {};
  }
  throw lastErr || new Error('Request failed');
}
